/* eslint-disable react/prop-types */
import React, { useEffect, useRef, useState } from 'react';
import { Table, Tabs, Tooltip } from 'antd';
import { SettingOutlined, PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { connect } from 'react-redux';
import { get } from 'lodash';
import { fetchAdminMaintenanceTypes } from '../../redux/admin/maintenance/action';
import MaintenanceChildList from './MaitenanceChildList';
import AddNewMaintenanceType from './AddNewMaintenanceType';

const { TabPane } = Tabs;

/** Max characters before showing "… read more" (single-line tabs, no vertical growth) */
const MAINTENANCE_TAB_LABEL_CHAR_LIMIT = 32;

/**
 * Horizontal scroll tab strip (matches CRC Toggle look) with ellipsis + read more for long names.
 */
const MaintenanceTypeTabStrip = ({
  maintenanceTypesList,
  activeTabId,
  onSelect,
  expandedTabId,
  setExpandedTabId,
}) => (
  <div className="maintenance-type-tab-strip" role="tablist" aria-label="Maintenance types">
    {maintenanceTypesList.map((t) => {
      const idStr = String(t.id);
      const name = (t.name != null && String(t.name)) || '';
      const isActive = activeTabId === idStr;
      const isExpanded = expandedTabId === idStr;
      const isLong = name.length > MAINTENANCE_TAB_LABEL_CHAR_LIMIT;

      return (
        <div
          key={idStr}
          role="tab"
          aria-selected={isActive}
          tabIndex={isActive ? 0 : -1}
          className={`maintenance-type-tab-btn ${isActive ? 'maintenance-type-tab-btn--active' : ''}`}
          onClick={() => {
            setExpandedTabId(null);
            onSelect(idStr);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setExpandedTabId(null);
              onSelect(idStr);
            }
          }}
        >
          <span className="maintenance-type-tab-btn-inner">
            {isLong && !isExpanded ? (
              <>
                <span className="maintenance-type-tab-text maintenance-type-tab-text--trunc">{`${name.slice(0, MAINTENANCE_TAB_LABEL_CHAR_LIMIT)}…`}</span>
                <button
                  type="button"
                  className="maintenance-type-tab-readlink"
                  aria-label={`Read full name: ${name}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setExpandedTabId(idStr);
                  }}
                >
                  read more
                </button>
              </>
            ) : null}
            {isLong && isExpanded ? (
              <>
                <span className="maintenance-type-tab-text maintenance-type-tab-text--full">{name}</span>
                <button
                  type="button"
                  className="maintenance-type-tab-readlink"
                  onClick={(e) => {
                    e.stopPropagation();
                    setExpandedTabId(null);
                  }}
                >
                  read less
                </button>
              </>
            ) : null}
            {!isLong ? <span className="maintenance-type-tab-text">{name}</span> : null}
          </span>
        </div>
      );
    })}
  </div>
);

const MaintenanceTypes = (props) => {
  const {
    onGetAdminMaintenanceTypes,
    adminMaintenanceTypes,
    isAdminMaintenanceLoading,
    equipmentTypeId,
    equipmentTypeName,
    isModernPage = false,
    maintenanceChecksSearch,
  } = props;

  const [maintenanceTypesList, SetMaintenanceTypesList] = useState([]);
  const [clickedClass, setClickedClass] = useState('settingIcon');
  const [clicked, setClicked] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalText, setModalText] = useState(false);
  const [update, setUpdate] = useState(false);
  const [isDelete, setIsDelete] = useState(false);
  const [tabChangeId, setTabChangeId] = useState(false);
  const [typeOfMaintenanceId, setTypeOfMaintenanceId] = useState('');
  const [expandedMaintenanceTabId, setExpandedMaintenanceTabId] = useState(null);

  const wrapperRef = useRef(null);

  /** Getting Maintenance Types Data on Load */
  useEffect(() => {
    onGetAdminMaintenanceTypes();
  }, []);

  /** Setting Maitenance types list to state */
  useEffect(() => {
    if (adminMaintenanceTypes && adminMaintenanceTypes.length > 0) {
      SetMaintenanceTypesList(adminMaintenanceTypes);
      const firstId = adminMaintenanceTypes[0].id;
      setTypeOfMaintenanceId(firstId);
      setTabChangeId(firstId);
    } else {
      SetMaintenanceTypesList([]);
      setTypeOfMaintenanceId('');
      setTabChangeId(false);
    }
  }, [adminMaintenanceTypes]);

  /** Settings for add/update/delete
   * Closing settings on click of mouse at anyplace in window
   */
  useEffect(() => {
    /**
     * Alert if clicked on outside of element
     */
    function handleClickOutsideType(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setClickedClass('settingIcon');
        setClicked(false);
      }
    }
    // Bind the event listener
    document.addEventListener('mousedown', handleClickOutsideType);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener('mousedown', handleClickOutsideType);
    };
  }, [wrapperRef]);

  /** Enable & Disable Add/Update/delete on Click */
  const handleClassName = () => {
    if (clicked) {
      setClickedClass('settingIcon');
      setClicked(false);
    } else {
      setClickedClass('settingIcon_clicked');
      setClicked(true);
    }
  };

  /** Function to set values and open add maintenance type modal */
  const addModal = () => {
    setModalOpen(true);
    setModalText('Add New Maintenance Type');
    setUpdate(false);
    setIsDelete(false);
  };

  /** Function to set values and open Update maintenance type modal */
  const editModal = () => {
    setModalOpen(true);
    setModalText('Update Maintenance Type');
    setUpdate(true);
    setIsDelete(false);
  };

  /** Function to set values and open Delete maintenance type modal */
  const deleteModal = () => {
    setModalOpen(true);
    setModalText('Delete Maintenance Type');
    setUpdate(false);
    setIsDelete(true);
  };

  const handleTabChange = (optionId) => {
    const id = typeof optionId === 'string' ? Number(optionId) : optionId;
    setExpandedMaintenanceTabId(null);
    setTabChangeId(id);
    setTypeOfMaintenanceId(id);
  };

  const activeTabId = typeOfMaintenanceId != null ? String(typeOfMaintenanceId) : (maintenanceTypesList[0] && String(maintenanceTypesList[0].id));

  return (
    <div className={isModernPage ? 'maintenance-types-modern' : ''}>
      {isModernPage ? (
        <>
          <div className="maintenance-types-modern-header">
            <div className="add_maintenance add_maintenance-modern" ref={wrapperRef}>
              {clicked && (
                <span className="modify_maitenance modify_maitenance-modern">
                  {/* <Tooltip title="Add New Maintenance Type" placement="bottom">
                    <PlusOutlined onClick={addModal} />
                  </Tooltip> */}
                  <Tooltip title="Update Maintenance Type" placement="bottom">
                    <EditOutlined onClick={editModal} />
                  </Tooltip>
                  <Tooltip title="Delete Maintenance Type" placement="bottom">
                    <DeleteOutlined onClick={deleteModal} />
                  </Tooltip>
                </span>
              )}
              <span
                className={`setting setting-modern ${clickedClass}`}
                onClick={handleClassName}
                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleClassName()}
                role="button"
                tabIndex={0}
              >
                <SettingOutlined />
                <span className="setting-modern-label">Settings</span>
              </span>
            </div>
            {isAdminMaintenanceLoading && <span className="maintenance-types-loading">Loading...</span>}
          </div>
          {!isAdminMaintenanceLoading && maintenanceTypesList.length > 0 && (
            <div className="maintenance-types-toggle-row">
              <MaintenanceTypeTabStrip
                maintenanceTypesList={maintenanceTypesList}
                activeTabId={activeTabId}
                onSelect={handleTabChange}
                expandedTabId={expandedMaintenanceTabId}
                setExpandedTabId={setExpandedMaintenanceTabId}
              />
            </div>
          )}
        </>
      ) : (
        <div className="add_maintenance" ref={wrapperRef}>
          {clicked && (
            <span className="modify_maitenance">
              {/* <Tooltip title="Add New Maintenance Type" placement="left">
                <PlusOutlined onClick={addModal} />
              </Tooltip> */}
              <Tooltip title="Update Maintenance Type" placement="left">
                <EditOutlined onClick={editModal} />
              </Tooltip>
              <Tooltip title="Delete Maintenance Type" placement="left">
                <DeleteOutlined onClick={deleteModal} />
              </Tooltip>
            </span>
          )}
          <SettingOutlined
            ref={wrapperRef}
            className={`setting ${clickedClass}`}
            onClick={handleClassName}
          />
        </div>
      )}
      {isModernPage ? (
        <div className="maintenance-types-modern-body">
          {maintenanceTypesList.length > 0 && typeOfMaintenanceId && (
            <MaintenanceChildList
              list={maintenanceTypesList.find((t) => t.id === typeOfMaintenanceId) || maintenanceTypesList[0]}
              typeOfMaintenanceId={typeOfMaintenanceId}
              equipmentTypeId={equipmentTypeId}
              equipmentTypeName={equipmentTypeName}
              tabChangeId={tabChangeId}
              isModernPage={isModernPage}
              maintenanceChecksSearch={maintenanceChecksSearch}
            />
          )}
          {isAdminMaintenanceLoading && <Table loading />}
          {!isAdminMaintenanceLoading && maintenanceTypesList.length === 0 && <Table />}
        </div>
      ) : (
        <Tabs
          className="custom-tab h-100 maintenance_tab"
          onTabClick={(key) => {
            setTabChangeId(key);
            setTypeOfMaintenanceId(key);
          }}
          activeKey={activeTabId}
        >
          {isAdminMaintenanceLoading ? (
            <TabPane className="tab_loading" tab="Loading..." key={1} style={{ position: 'relative' }}>
              <Table loading />
            </TabPane>
          ) : maintenanceTypesList.length ? (
            maintenanceTypesList.map((types) => (
              <TabPane tab={types.name} key={String(types.id)} style={{ position: 'relative' }}>
                <MaintenanceChildList
                  list={types}
                  typeOfMaintenanceId={typeOfMaintenanceId}
                  equipmentTypeId={equipmentTypeId}
                  equipmentTypeName={equipmentTypeName}
                  tabChangeId={tabChangeId}
                  isModernPage={false}
                  maintenanceChecksSearch={maintenanceChecksSearch}
                />
              </TabPane>
            ))
          ) : (
            <TabPane className="tab_loading" tab="No Data" key={1} style={{ position: 'relative' }}>
              <Table />
            </TabPane>
          )}
        </Tabs>
      )}

      {/* Modal to create, update, delete  maintenance types */}
      <div>
        <AddNewMaintenanceType
          modalOpen={modalOpen}
          setModalOpen={setModalOpen}
          modalText={modalText}
          update={update}
          maintenanceTypesList={maintenanceTypesList}
          isDelete={isDelete}
          isModernPage={isModernPage}
        />
      </div>
    </div>
  );
};
const mapStateToProps = ({ maintenanceAdminReducer }) => {
  const adminMaintenanceTypes = get(maintenanceAdminReducer, 'adminMaintenanceTypes', []);
  const isAdminMaintenanceLoading = get(
    maintenanceAdminReducer,
    'isAdminMaintenanceLoading',
    false
  );
  return {
    adminMaintenanceTypes,
    isAdminMaintenanceLoading,
  };
};

const mapDispatchToProps = {
  onGetAdminMaintenanceTypes: fetchAdminMaintenanceTypes,
};

export default connect(mapStateToProps, mapDispatchToProps)(MaintenanceTypes);
